import {
  Box,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Typography,
  Grid2,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";

// Multi-lingual support
import { t } from "i18next";

// Validation schemas
import { getBasicDetailsValidationSchema } from "./BasicDetailsValidationSchema";

// API Manager
import { getApi } from "../../../../../services/apiManager/ApiManager";

// Basic details types
import {
  EOperationType,
  IProjectList,
  MAX_IMAGE_SIZE,
} from "./BasicDetailsTypes";
import { TypeOfList } from "../../../../../components/shared/commonSearchSelect/CommonSearchSelectTypes";

// Components
import CommonSearchSelect from "../../../../../components/shared/commonSearchSelect/CommonSearchSelect";
import CommonFileUploadHint from "../../../../shared/commonFileUploadInfo/CommonFileUploadInfo";

// Hooks
import { useSearchParams } from "react-router-dom";
import useTrimContent from "../../../../../hooks/useTrimContent/useTrimContent";
import { useNumericInput } from "../../../../../hooks/useNationalIdInput/useNationalIdInput";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useToaster } from "../../../../../hooks/useToasterContext/useToasterContext";

const BasicDetails = ({
  setValidateStep,
  onSubmit,
  onFieldChange,
  farmerData,
  apiErrorCode,
  onFileDownload,
}: any) => {
  // Check if url have query parameters for edit case
  const [searchParams] = useSearchParams();
  const farmerId = searchParams.get("farmerId");
  // Get and set project list
  const [projectList, setProjectList] = useState<IProjectList[]>([]);
  // Get and set agent list
  const [agentList, setAgentList] = useState<IProjectList[]>([]);

  // Manage upload farmer photo
  const [selectedFarmerPhoto, setSelectedFarmerPhoto] = useState<File | null>(
    null,
  );
  const [selectedFarmerPhotoName, setSelectedFarmerPhotoName] =
    useState<string>("");

  // Toaster
  const { setShowToaster } = useToaster();

  // Use the useFormik hook to manage the form
  const formik = useFormik({
    initialValues: {
      agentId: "",
      operationType: EOperationType.INDIVIDUAL,
      name: "",
      lastName: "",
      nationalId: "",
      dateOfBirth: null,
      internalId: "",
      projectId: "",
      selectedFarmerPhoto: selectedFarmerPhoto,
      selectedFarmerPhotoName: selectedFarmerPhotoName,
      selectedFarmerPhotoSignedUrl: "",
    },
    validationSchema: getBasicDetailsValidationSchema(),
    onSubmit: (values) => {
      const farmerData: any = { ...values };

      if (
        selectedFarmerPhoto &&
        selectedFarmerPhoto.type.startsWith("image/") &&
        selectedFarmerPhoto.size > MAX_IMAGE_SIZE
      ) {
        return;
      }

      onSubmit(farmerData);
    },
  });

  // Handle trim on blur
  const handleTrimOnBlur = useTrimContent(formik);

  // Get meta data and set existing data
  useEffect(() => {
    // Get Project list
    const getProjects = async () => {
      const response = await getApi(`/v1/project/lookup`);
      if (response.status) {
        setProjectList(response.data);
      }
    };
    getProjects();

    // Get agent list
    const getAgents = async () => {
      const response = await getApi(`/v1/user/lookup/agents`);
      if (response.status) {
        setAgentList(response.data);
      }
    };
    getAgents();

    // Edit case handling
    if (farmerData && farmerData) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        agentId: farmerData?.agentId || "",
        name: farmerData?.name || "",
        lastName: farmerData?.lastName || "",
        operationType: farmerData?.operationType || EOperationType.INDIVIDUAL,
        nationalId: farmerData?.nationalId || "",
        dateOfBirth: farmerData?.dateOfBirth || null,
        internalId: farmerData?.internalId || "",
        projectId: farmerData?.projectId || "",
        selectedFarmerPhoto: farmerData?.selectedFarmerPhoto || null,
        selectedFarmerPhotoName: farmerData?.selectedFarmerPhotoName || "",
        selectedFarmerPhotoSignedUrl:
          farmerData?.selectedFarmerPhotoSignedUrl || "",
        mediaFileId: farmerData?.mediaFileId || null,
      }));

      setSelectedFarmerPhoto(farmerData?.selectedFarmerPhoto);
      setSelectedFarmerPhotoName(farmerData?.selectedFarmerPhotoName);
    }
  }, [farmerData]);

  // Step validation
  useEffect(() => {
    if (setValidateStep) {
      setValidateStep(() => formik.submitForm);
    }
  }, [formik.submitForm, setValidateStep]);

  // Handle api error code
  useEffect(() => {
    console.log(apiErrorCode);
  }, [apiErrorCode]);

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      if (file.size > MAX_IMAGE_SIZE) {
        setShowToaster(
          true,
          t("addUpdateField.mediaFile.imageFileSizeLimit"),
          "error",
        );
        const fileInput = document.getElementById(
          "farmerPhoto",
        ) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = "";
        }
        return;
      }
      setSelectedFarmerPhoto(file);
      setSelectedFarmerPhotoName(file.name);

      formik.setFieldValue("selectedFarmerPhoto", file);
      formik.setFieldValue("selectedFarmerPhotoName", file.name);
    }
  };

  // Handle operation type change event
  const handleOperationTypeChange = (event: any) => {
    if (event?.target?.value) {
      formik.setFieldValue("operationType", event.target.value);
    }
  };

  // Handle remove selected file
  const handleRemoveSelectedFile = () => {
    setSelectedFarmerPhoto(null);
    setSelectedFarmerPhotoName("");

    formik.setFieldValue("selectedFarmerPhoto", null);
    formik.setFieldValue("selectedFarmerPhotoName", "");

    // Reset file input value
    const fileInput = document.getElementById(
      "farmerPhoto",
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }

    // Delete media file id from farmer data
    if (farmerData && farmerData.mediaFileId) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        mediaFileId: null,
      }));
    }
  };

  // National Id input hook
  const handleNumericInput = useNumericInput(13);

  // Handle download selected file
  const handleDownloadSelectedFile = () => {
    // Check if a selected file is available; if not, check if the farmer photo URL is available
    if (selectedFarmerPhoto) {
      onFileDownload(selectedFarmerPhoto, selectedFarmerPhotoName);
    } else if (farmerData?.selectedFarmerPhotoSignedUrl) {
      onFileDownload(
        farmerData.selectedFarmerPhotoSignedUrl,
        farmerData.selectedFarmerPhotoName,
      );
    }
  };

  return (
    <Box sx={{ paddingLeft: 2 }}>
      {/* Form Container */}
      <Box
        sx={{
          backgroundColor: "farm.contrastText",
          maxWidth: "auto",
          height: "var(--AgentWebHeight-farmerstepper)",
          overflowY: "auto",
        }}
      >
        {/* Radio Button Group - Select Operation Type */}
        <Box
          mt={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Typography variant="body2">
            {t("addUpdateFarmer.selectOperationType")}
          </Typography>
          <RadioGroup
            row
            name="operationType"
            value={formik.values.operationType}
            onChange={handleOperationTypeChange}
          >
            <FormControlLabel
              value={EOperationType.INDIVIDUAL}
              control={<Radio />}
              label={t("addUpdateFarmer.individual")}
            />
            <FormControlLabel
              value={EOperationType.INSTITUTION}
              control={<Radio />}
              label={t("addUpdateFarmer.institution")}
            />
          </RadioGroup>
        </Box>
        <Grid2 container spacing={2} mt={3}>
          <Grid2 size={3}>
            {agentList && (
              <CommonSearchSelect
                list={agentList}
                formik={formik}
                label={<span>{t("addUpdateFarmer.agent")}*</span>}
                currentValue={formik.values.agentId}
                formPath={"agentId"}
                width={"100%"}
                type={TypeOfList.ArrayOfObject}
              />
            )}
          </Grid2>
        </Grid2>
        {/* Name Fields */}
        <Grid2 container spacing={2} mt={3}>
          <Grid2 size={3}>
            <TextField
              fullWidth
              size="small"
              label={`${t("addUpdateFarmer.firstName")}*`}
              variant="filled"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={handleTrimOnBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid2>
          <Grid2 size={3}>
            <TextField
              fullWidth
              size="small"
              label={`${t("addUpdateFarmer.lastName")}*`}
              variant="filled"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={handleTrimOnBlur}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </Grid2>
        </Grid2>

        {/* National ID & Date of Birth */}
        <Grid2 container spacing={2} mt={3}>
          <Grid2 size={3}>
            <TextField
              fullWidth
              size="small"
              label={`${t("addUpdateFarmer.nationalId")}`}
              variant="filled"
              name="nationalId"
              value={formik.values.nationalId}
              onChange={(e) => {
                handleNumericInput(e, formik.handleChange);
                onFieldChange();
              }}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.nationalId &&
                  Boolean(formik.errors.nationalId)) ||
                apiErrorCode === "E_FARMER_DUPLICATE_NATIONAL_ID"
              }
              helperText={formik.touched.nationalId && formik.errors.nationalId}
            />
            {/* API Error Message */}
            {apiErrorCode &&
              apiErrorCode === "E_FARMER_DUPLICATE_NATIONAL_ID" && (
                <Box
                  sx={{
                    color: "agricolors.error",
                    fontSize: "12px",
                    marginLeft: "14px",
                  }}
                >
                  {t("addUpdateFarmer.duplicateNationalId")}
                </Box>
              )}
          </Grid2>
          <Grid2 size={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label={`${t("addUpdateFarmer.dateOfBirth")}`}
                format="DD-MM-YYYY"
                value={
                  formik.values.dateOfBirth
                    ? dayjs(formik.values.dateOfBirth)
                    : null
                }
                onChange={(newValue) => {
                  formik.setFieldValue("dateOfBirth", newValue?.toDate() ?? "");
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    variant: "filled",
                    error: Boolean(
                      formik.touched.dateOfBirth && formik.errors.dateOfBirth,
                    ),
                    helperText:
                      formik.touched.dateOfBirth && formik.errors.dateOfBirth,
                  },
                  openPickerButton: {
                    className: "icon-datepicker",
                  },
                }}
                slots={{
                  openPickerIcon: () => null, // Hides default icon
                }}
              />
            </LocalizationProvider>
          </Grid2>
        </Grid2>

        {/* Internal Farmer ID & Project Selection */}
        <Grid2 container spacing={2} mt={3}>
          <Grid2 size={3}>
            <TextField
              fullWidth
              size="small"
              label={t("addUpdateFarmer.internalFarmerId")}
              variant="filled"
              name="internalId"
              value={formik.values.internalId}
              onChange={(e) => {
                formik.handleChange(e);
                onFieldChange();
              }}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.internalId &&
                  Boolean(formik.errors.internalId)) ||
                apiErrorCode === "E_FARMER_DUPLICATE_INTERNAL_ID"
              }
              helperText={formik.touched.internalId && formik.errors.internalId}
            />
            {/* API Error Message */}
            {apiErrorCode &&
              apiErrorCode === "E_FARMER_DUPLICATE_INTERNAL_ID" && (
                <Box
                  sx={{
                    color: "agricolors.error",
                    fontSize: "12px",
                    marginLeft: "14px",
                  }}
                >
                  {t("addUpdateFarmer.duplicateInternalId")}
                </Box>
              )}
          </Grid2>
          <Grid2 size={3}>
            {projectList && (
              <CommonSearchSelect
                list={projectList}
                formik={formik}
                label={<span>{`${t("addUpdateFarmer.project")}*`}</span>}
                currentValue={formik.values.projectId}
                formPath={"projectId"}
                width={"100%"}
                type={TypeOfList.ArrayOfObject}
                disabled={!!farmerId}
              />
            )}
          </Grid2>
        </Grid2>

        {/* File Upload Button - Upload Photo */}
        <Grid2 container spacing={2} mt={3}>
          <Grid2 size={6}>
            {(!selectedFarmerPhotoName || selectedFarmerPhotoName === "") && (
              <Button
                variant="contained"
                component="label"
                fullWidth
                color="fileuploadbtn"
                className="custom-fileupload-btn"
                startIcon={<i className="icon-upload"></i>}
                sx={{ boxShadow: "none", "&:hover": { boxShadow: "none" } }}
              >
                {t("addUpdateFarmer.uploadPhoto")}
                <input
                  id="farmerPhoto"
                  type="file"
                  accept=".png, .jpg, .jpeg"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
            )}

            {selectedFarmerPhotoName && selectedFarmerPhotoName !== "" && (
              <Box
                my={1}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  fontSize={"14px"}
                  className="withoutspace-text-wrap"
                  sx={{
                    cursor: "pointer",
                    color: "blue",
                    textDecoration: "underline",
                  }}
                  onClick={handleDownloadSelectedFile}
                >
                  {selectedFarmerPhotoName}
                </Box>

                <IconButton
                  sx={{ marginRight: 1 }}
                  onClick={handleRemoveSelectedFile}
                >
                  <i className="icon-close" />
                </IconButton>
              </Box>
            )}
            {!selectedFarmerPhotoName && (
              <CommonFileUploadHint
                supportedTypes={["PNG, JPG"]}
                maxFiles={1}
                isImageOrVideo={false}
              />
            )}
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default BasicDetails;
